import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getVersion, getQuestions, getComponents,
    createQuestion, updateQuestion, deleteQuestion,
    createComponent, updateComponent, deleteComponent,
    createOption, deleteOption,
    duplicateQuestion
} from '../../../api/autoEstimate';
import QuestionDetailPanel from './QuestionDetailPanel';
import ComponentEditor from './ComponentEditor';
import EstimateHelpModal from './EstimateHelpModal';
import AdminLayout from '../../../components/admin/AdminLayout';
import AdminButton from '../../../components/admin/AdminButton';


const EstimateDetail = () => {
    const { versionId } = useParams();
    const navigate = useNavigate();
    const [version, setVersion] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [components, setComponents] = useState([]);
    const [activeTab, setActiveTab] = useState('questions'); // questions, components
    const [loading, setLoading] = useState(true);

    // Component Editor State
    const [editingComponent, setEditingComponent] = useState(null);
    const [showHelp, setShowHelp] = useState(false);

    // Inline Edit State for Questions
    // editingQId is now only used as a sentinel 'NEW' for the draft row that
    // appears when a user clicks "새 질문 추가". Existing rows are always
    // inline-editable and persist via the per-row Save button when dirty.
    const [editingQId, setEditingQId] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    // Lifted draft state for existing rows. Question fields and option labels
    // both live here so a single Save button can persist them in one unified
    // PUT /admin/questions/:id call (now accepts optionLabels in the body).
    // The shape mirrors the server data so dirty-detection is a direct compare.
    const [rowDrafts, setRowDrafts] = useState({}); // { [qId]: { section, title } }
    const [optionLabelDrafts, setOptionLabelDrafts] = useState({}); // { [qId]: { [optId]: label } }
    const [savingRowId, setSavingRowId] = useState(null);

    // Expansion State for Detail Panel
    const [expandedQId, setExpandedQId] = useState(null);

    // Reordering State
    const [isReordering, setIsReordering] = useState(false);
    const [dragItemIndex, setDragItemIndex] = useState(null);
    const [isSavingOrder, setIsSavingOrder] = useState(false);

    // Condition Mode State
    const [isConditionMode, setIsConditionMode] = useState(false);
    const [conditionTargetId, setConditionTargetId] = useState(null);

    useEffect(() => {
        loadData();
    }, [versionId]);

    // Sync row drafts and option-label drafts from `questions`. Critically this
    // is merge-preserving: a row/option that the user has actively edited keeps
    // its in-progress text; anything else snaps to the server value. Without
    // this guard, every parent loadData() refresh would wipe pending edits.
    useEffect(() => {
        setRowDrafts(prev => {
            const next = {};
            questions.forEach(q => {
                const baseline = { section: q.section || '', title: q.title || '' };
                const draft = prev[q.id];
                if (draft && (draft.section !== q.section || draft.title !== q.title)) {
                    next[q.id] = draft; // keep user's pending edits
                } else {
                    next[q.id] = baseline;
                }
            });
            return next;
        });
        setOptionLabelDrafts(prev => {
            const next = {};
            questions.forEach(q => {
                const perQ = {};
                (q.options || []).forEach(o => {
                    const draftLabel = prev[q.id]?.[o.id];
                    if (draftLabel !== undefined && draftLabel !== o.label) {
                        perQ[o.id] = draftLabel; // keep user's pending edit
                    } else {
                        perQ[o.id] = o.label;
                    }
                });
                next[q.id] = perQ;
            });
            return next;
        });
    }, [questions]);

    const isRowDirty = (q) => {
        const rd = rowDrafts[q.id];
        if (rd && (rd.section !== q.section || rd.title !== q.title)) return true;
        const od = optionLabelDrafts[q.id] || {};
        return (q.options || []).some(o => od[o.id] !== undefined && od[o.id] !== o.label);
    };

    const handleRowFieldChange = (qId, field, value) => {
        setRowDrafts(prev => ({
            ...prev,
            [qId]: { ...(prev[qId] || {}), [field]: value },
        }));
    };

    const handleOptionLabelChange = (qId, optId, value) => {
        setOptionLabelDrafts(prev => ({
            ...prev,
            [qId]: { ...(prev[qId] || {}), [optId]: value },
        }));
    };

    const handleSaveRow = async (q) => {
        if (savingRowId) return;
        const rd = rowDrafts[q.id] || {};
        const od = optionLabelDrafts[q.id] || {};

        const payload = {};
        if (rd.section !== undefined && rd.section !== q.section) payload.section = rd.section;
        if (rd.title !== undefined && rd.title !== q.title) payload.title = rd.title;

        const dirtyOptionLabels = (q.options || [])
            .filter(o => od[o.id] !== undefined && od[o.id] !== o.label)
            .map(o => ({ id: o.id, label: od[o.id] }));

        if (Object.keys(payload).length === 0 && dirtyOptionLabels.length === 0) return;

        const blank = dirtyOptionLabels.find(u => (u.label || '').trim() === '');
        if (blank) {
            return alert('빈 옵션명은 저장할 수 없습니다.');
        }
        if (payload.title !== undefined && payload.title.trim() === '') {
            return alert('질문 제목은 비워둘 수 없습니다.');
        }

        if (dirtyOptionLabels.length > 0) {
            payload.optionLabels = dirtyOptionLabels;
        }

        setSavingRowId(q.id);
        try {
            await updateQuestion(q.id, payload);
            await loadData(true);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || '저장 실패';
            alert('저장 실패: ' + msg);
        } finally {
            setSavingRowId(null);
        }
    };

    const handleDiscardRow = (q) => {
        if (!isRowDirty(q)) return;
        if (!window.confirm('이 행의 저장되지 않은 변경을 모두 되돌립니다. 계속하시겠습니까?')) return;
        setRowDrafts(prev => ({
            ...prev,
            [q.id]: { section: q.section || '', title: q.title || '' },
        }));
        setOptionLabelDrafts(prev => {
            const perQ = {};
            (q.options || []).forEach(o => { perQ[o.id] = o.label; });
            return { ...prev, [q.id]: perQ };
        });
    };

    // Guard against accidental tab close / reload with unsaved row edits
    useEffect(() => {
        const anyDirty = questions.some(q => isRowDirty(q));
        if (!anyDirty) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questions, rowDrafts, optionLabelDrafts]);

    const handleDragStart = (e, index) => {
        setDragItemIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (dragItemIndex === null) return;
        if (dragItemIndex === index) return;

        const newQuestions = [...questions];
        const draggedItem = newQuestions[dragItemIndex];
        newQuestions.splice(dragItemIndex, 1);
        newQuestions.splice(index, 0, draggedItem);

        setDragItemIndex(index);
        setQuestions(newQuestions);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragItemIndex(null);
    };

    const handleSaveOrder = async () => {
        if (!window.confirm('변경된 순서를 저장하시겠습니까?')) return;
        setIsSavingOrder(true);
        try {
            const updatePromises = questions.map((q, idx) => {
                const newSortOrder = idx + 1;
                const updateData = {};

                // 1. Order Change
                if (q.sortOrder !== newSortOrder) {
                    updateData.sortOrder = newSortOrder;
                }

                // 2. First Question Mandatory Reset
                if (idx === 0 && q.conditionJson) {
                    updateData.conditionJson = null;
                }

                if (Object.keys(updateData).length > 0) {
                    return updateQuestion(q.id, updateData);
                }
                return Promise.resolve();
            });

            await Promise.all(updatePromises);
            setIsReordering(false);
            await loadData(true);
        } catch (error) {
            console.error(error);
            alert('순서 저장 실패');
        } finally {
            setIsSavingOrder(false);
        }
    };

    const loadData = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [vData, qData, cData] = await Promise.all([
                getVersion(versionId),
                getQuestions(versionId),
                getComponents(versionId)
            ]);
            setVersion(vData);
            setQuestions(qData);
            setComponents(cData);
        } catch (error) {
            console.error(error);
            alert('데이터 로드 실패');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    // --- Inline & Expansion Handlers ---

    const handleExpandToggle = (id) => {
        if (expandedQId === id) setExpandedQId(null);
        else setExpandedQId(id);
    };

    const handleInlineChange = (field, value) => {
        setEditFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleInlineSave = async (data) => {
        try {
            if (editingQId === 'NEW' || data.isNew) {
                // Create Mode
                const { isNew, ...createData } = data; // Remove internal flag
                await createQuestion(versionId, createData);
            } else {
                // Update Mode
                await updateQuestion(data.id, data);
            }
            setEditingQId(null);
            loadData(true);
        } catch (err) {
            console.error(err);
            alert('저장 실패: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleInlineCancel = () => {
        setEditingQId(null);
        setEditFormData({});
    };

    const handleAddQuestion = () => {
        const newQ = {
            versionId: parseInt(versionId),
            section: 'General',
            title: '',
            type: 'RADIO',
            sortOrder: questions.length + 1,
            isNew: true // Flag to indicate new question
        };
        setEditingQId('NEW'); // Use special ID for new
        setEditFormData(newQ);
    };

    const handleDeleteQuestion = async (id) => {
        if (!window.confirm('질문을 삭제하시겠습니까?')) return;
        try {
            await deleteQuestion(id);
            loadData(true);
        } catch (err) {
            alert('질문 삭제 실패');
        }
    };

    // --- Component Handlers ---

    const handleSaveComponent = async (cData) => {
        try {
            if (cData.id) {
                await updateComponent(cData.id, cData);
            } else {
                await createComponent(versionId, cData);
            }
            setEditingComponent(null);
            loadData(true);
        } catch (err) {
            // Check for duplicate code error (409 Conflict)
            if (err.response && err.response.status === 409) {
                alert(err.response.data.message || '이미 존재하는 코드입니다.');
            } else {
                const msg = err.response?.data?.message || '가격 요소 저장 실패';
                alert(msg);
            }
        }
    };

    const handleDeleteComponent = async (id) => {
        if (!window.confirm('가격 요소를 삭제하시겠습니까?')) return;
        try {
            await deleteComponent(id);
            loadData(true);
        } catch (err) {
            alert('가격 요소 삭제 실패');
        }
    };

    // --- Condition Mode Handlers ---

    const handleSetCondition = async (targetId, parentId, optionId) => {
        try {
            const targetQ = questions.find(q => q.id === targetId);
            if (!targetQ) return;

            const newCondition = {
                dependsOnQuestionId: parentId,
                requiredOptionId: optionId
            };

            await updateQuestion(targetId, { conditionJson: newCondition });
            await loadData(true);
        } catch (err) {
            console.error(err);
            alert('조건 설정 실패');
        }
    };

    const handleClearCondition = async (targetId) => {
        if (!window.confirm('표시 조건을 삭제하시겠습니까? (항상 표시됨)')) return;
        try {
            await updateQuestion(targetId, { conditionJson: null });
            await loadData(true);
        } catch (err) {
            console.error(err);
            alert('조건 삭제 실패');
        }
    };

    const handleDuplicate = async (qId) => {
        if (!window.confirm('질문을 복제하시겠습니까?')) return;
        try {
            await duplicateQuestion(qId);
            loadData(true);
        } catch (err) {
            alert('복제 실패: ' + err.message);
        }
    };

    // --- Excel Handlers ---
    const handleExportExcel = async () => {
        // Fetch fresh data to ensure all recent changes are included
        const freshQuestions = await getQuestions(versionId);

        const header = ['ID', 'Order', 'Section', 'Title', 'Type', 'Condition', 'Options'];
        const data = freshQuestions.map(q => {
            // Condition: ParentTitle:OptionLabel
            let conditionStr = '';
            if (q.conditionJson?.dependsOnQuestionId) {
                const parent = freshQuestions.find(p => p.id === q.conditionJson.dependsOnQuestionId);
                const opt = parent?.options?.find(o => o.id === q.conditionJson.requiredOptionId);
                if (parent && opt) {
                    conditionStr = `${parent.title}:${opt.label}`;
                }
            }

            // Options: Label Only (No Pricing Info exported)
            const optionStr = q.options?.map(o => o.label).join(' | ') || '';

            return {
                'ID': q.id,
                'Order': q.sortOrder,
                'Section': q.section,
                'Title': q.title,
                'Type': q.type,
                'Condition': conditionStr,
                'Options': optionStr
            };
        });

        const ws = XLSX.utils.json_to_sheet(data, { header });
        // Adjust column widths
        ws['!cols'] = [{ wch: 5 }, { wch: 6 }, { wch: 15 }, { wch: 30 }, { wch: 10 }, { wch: 25 }, { wch: 50 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Questions');
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(dataBlob, `estimate_v${versionId}_questions.xlsx`);
    };

    const handleImportExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!window.confirm('⚠️ 경고: 엑셀 가져오기는 현재 설정을 덮어씁니다.\n특히 옵션 및 가격 설정이 초기화될 수 있습니다.\n계속하시겠습니까?')) {
            e.target.value = '';
            return;
        }

        setLoading(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                // Pre-fetch Data Maps
                const refreshMaps = async () => {
                    const [qs, comps] = await Promise.all([
                        getQuestions(versionId),
                        getComponents()
                    ]);
                    const qMap = {}; // ID -> Q
                    const titleMap = {}; // Title -> Q
                    qs.forEach(q => {
                        qMap[q.id] = q;
                        titleMap[q.title] = q;
                    });

                    const compMap = {}; // Code -> Comp
                    comps.forEach(c => compMap[c.code] = c);

                    return { qMap, titleMap, compMap };
                };

                // eslint-disable-next-line no-unused-vars
                let { qMap, titleMap, compMap } = await refreshMaps();

                // 1. Upsert Questions & Sync Options
                for (const row of data) {
                    const rowId = row['ID'];
                    const title = row['Title'];
                    if (!title) continue;

                    const qData = {
                        versionId: parseInt(versionId),
                        section: row['Section'] || 'General',
                        title: title,
                        type: row['Type'] || 'RADIO',
                        sortOrder: parseInt(row['Order']) || 999
                    };

                    let qId = null;
                    // eslint-disable-next-line no-unused-vars
                    let existingQ = null;

                    // Find existing by ID first, then Title
                    if (rowId && qMap[rowId]) {
                        qId = rowId;
                        existingQ = qMap[rowId];
                        await updateQuestion(qId, qData);
                    } else if (titleMap[title]) {
                        // Fallback: match by title if ID is missing (e.g. new row in Excel)
                        qId = titleMap[title].id;
                        existingQ = titleMap[title];
                        await updateQuestion(qId, qData);
                    } else {
                        // Create New
                        const newQ = await createQuestion(versionId, qData);
                        qId = newQ.id;
                    }

                    // Sync Options
                    if (row['Options'] !== undefined) {
                        // Fetch latest to get current options/effects
                        const latestQs = await getQuestions(versionId);
                        const freshQ = latestQs.find(q => q.id === qId);
                        const oldOpts = freshQ ? freshQ.options : [];

                        const newOptsRaw = String(row['Options']).split('|');
                        const newOptLabels = [];

                        for (const rawOpt of newOptsRaw) {
                            const label = rawOpt.trim();
                            if (!label) continue;

                            newOptLabels.push(label);

                            let currentOpt = oldOpts.find(o => o.label === label);
                            if (!currentOpt) {
                                // Create New Option
                                currentOpt = await createOption(qId, { label });
                            }
                            // Existing Option: Preserve existing pricing effects (Do not touch)
                        }

                        // Delete removed options
                        for (const old of oldOpts) {
                            if (!newOptLabels.includes(old.label)) {
                                await deleteOption(old.id);
                            }
                        }
                    }
                }

                // Refresh maps for linking conditions
                const mapData = await refreshMaps();
                titleMap = mapData.titleMap;

                // 2. Update Conditions
                for (const row of data) {
                    // We need to identify the Q again
                    let q = null;
                    if (row['ID']) q = mapData.qMap[row['ID']];
                    else q = titleMap[row['Title']];

                    if (!q) continue;

                    const condStr = row['Condition'];
                    if (!condStr) {
                        // Only clear if it was set? Or always clear if empty in Excel?
                        // If Excel cell is empty, user likely implies no condition.
                        if (q.conditionJson) await updateQuestion(q.id, { conditionJson: null });
                        continue;
                    }

                    if (condStr.includes(':')) {
                        const [pTitle, oLabel] = condStr.split(':');
                        const parent = titleMap[pTitle.trim()];
                        if (parent) {
                            const opt = parent.options.find(o => o.label === oLabel.trim());
                            if (opt) {
                                const newCond = { dependsOnQuestionId: parent.id, requiredOptionId: opt.id };
                                await updateQuestion(q.id, { conditionJson: newCond });
                            }
                        }
                    }
                }

                await loadData(true);
                alert('가져오기 완료');
            } catch (err) {
                console.error(err);
                alert('오류: ' + err.message);
            } finally {
                setLoading(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    // --- Component Excel Handlers ---

    const handleExportComponentsExcel = () => {
        const rows = components.map((c, idx) => ({
            'No': idx + 1,
            'Code': c.code,
            'Name': c.name,
            'Admin Memo': c.adminMemo,
            'Default Price': c.defaultPrice
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Components");
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        saveAs(data, `estimate_components.xlsx`);
    };

    const handleImportComponentsExcel = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                if (data.length === 0) { alert('No data'); return; }
                if (!window.confirm(`총 ${data.length}개의 가격 요소를 가져오시겠습니까?`)) return;

                for (const row of data) {
                    const code = row['Code'];
                    const name = row['Name'];
                    if (!code || !name) continue;

                    const adminMemo = row['Admin Memo'] || '';
                    const defaultPrice = parseInt(row['Default Price']) || 0;

                    const exist = components.find(c => c.code === code);
                    if (exist) {
                        await updateComponent(exist.id, { name, adminMemo, defaultPrice });
                    } else {
                        await createComponent(0, { code, name, adminMemo, defaultPrice });
                    }
                }
                loadData(true);
                alert('가져오기 완료');
            } catch (err) {
                console.error(err);
                alert('가져오기 실패');
            } finally {
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    // --- Unified Excel Handlers ---

    const handleUnifiedExport = () => {
        if (activeTab === 'questions') {
            handleExportExcel();
        } else if (activeTab === 'components') {
            handleExportComponentsExcel();
        }
    };

    const handleUnifiedImport = (e) => {
        if (activeTab === 'questions') {
            handleImportExcel(e);
        } else if (activeTab === 'components') {
            handleImportComponentsExcel(e);
        }
    };

    if (loading) return <div>Loading...</div>;
    if (!version) return <div>버전을 찾을 수 없습니다.</div>;

    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div className="flex items-center gap-4 flex-wrap">
                        <AdminButton 
                            variant="secondary" size="sm" 
                            onClick={() => navigate('/admin/auto-estimate')}
                        >
                            &lt; 목록
                        </AdminButton>
                        <h1 className="text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2 m-0">
                            {version.name} 
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                version.status === 'ACTIVE' ? 'bg-green-100 text-green-800 border-green-200' :
                                version.status === 'DRAFT' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                                'bg-gray-100 text-gray-800 border-gray-200'
                            }`}>
                                {version.status}
                            </span>
                        </h1>
                        <button
                            onClick={() => setShowHelp(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-full text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm ml-2"
                        >
                            <span role="img" aria-label="help">❓</span> 도움말
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <input type="file" id="excelInput" hidden accept=".xlsx, .xls" onChange={handleUnifiedImport} />
                        <AdminButton 
                            variant="secondary" 
                            onClick={() => document.getElementById('excelInput').click()}
                        >
                            📥 {activeTab === 'questions' ? '질문 엑셀' : '가격요소 엑셀'} 가져오기
                        </AdminButton>
                        <AdminButton 
                            variant="primary" 
                            onClick={handleUnifiedExport}
                        >
                            📊 {activeTab === 'questions' ? '질문 엑셀' : '가격요소 엑셀'} 내보내기
                        </AdminButton>
                    </div>
                </header>

                <div className="flex space-x-2 border-b border-gray-200 mb-6 w-full overflow-x-auto">
                    <button
                        className={`whitespace-nowrap py-3 px-6 text-sm font-medium border-b-2 transition-colors focus:outline-none ${
                            activeTab === 'questions' 
                            ? 'border-blue-600 text-blue-600 bg-white rounded-t-lg' 
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 rounded-t-lg'
                        }`}
                        onClick={() => setActiveTab('questions')}
                    >
                        질문 관리
                    </button>
                    <button
                        className={`whitespace-nowrap py-3 px-6 text-sm font-medium border-b-2 transition-colors focus:outline-none ${
                            activeTab === 'components' 
                            ? 'border-blue-600 text-blue-600 bg-white rounded-t-lg' 
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 rounded-t-lg'
                        }`}
                        onClick={() => setActiveTab('components')}
                    >
                        가격 요소(Pricing)
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-0 sm:p-4 overflow-hidden">
                    {activeTab === 'questions' && (
                        <div onClick={() => { if (expandedQId !== null) setExpandedQId(null); }}>
                            {/* Control Bar */}
                            <div className="flex justify-end gap-2 mb-4 p-2 sm:p-0">
                                {!isReordering && !isConditionMode ? (
                                    <>
                                        <AdminButton variant="secondary" size="sm" onClick={() => setIsReordering(true)}>순서 변경</AdminButton>
                                        <AdminButton variant="outline" size="sm" className="border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100" onClick={() => { setIsConditionMode(true); setExpandedQId(null); }}>
                                            ⚡ 표시조건 설정
                                        </AdminButton>
                                    </>
                                ) : isReordering ? (
                                    <div className="flex gap-2 w-full justify-between items-center bg-blue-50 p-2 rounded-md">
                                        <span className="text-blue-800 font-medium text-sm ml-2">블록을 드래그하여 순서를 변경하세요</span>
                                        <div className="flex gap-2">
                                            <AdminButton variant="primary" size="sm" onClick={handleSaveOrder} disabled={isSavingOrder}>
                                                {isSavingOrder ? '저장 중...' : '💾 순서 저장'}
                                            </AdminButton>
                                            <AdminButton variant="secondary" size="sm" onClick={() => { setIsReordering(false); loadData(true); }} disabled={isSavingOrder}>취소</AdminButton>
                                        </div>
                                    </div>
                                ) : (
                                    /* Condition Mode Controls */
                                    <div className="flex items-center justify-between gap-4 bg-purple-50 px-4 py-3 rounded-md border border-purple-100 w-full shadow-inner">
                                        <div className="flex items-center gap-4">
                                            <span className="font-bold text-purple-700 text-sm whitespace-nowrap">⚡ 표시조건 설정 모드</span>
                                            <div className="text-sm text-gray-600 hidden sm:block">
                                                {conditionTargetId
                                                    ? "설정할 질문의 '상위 질문 옵션'을 클릭하여 연결하세요."
                                                    : "설정하려는 질문의 '표시 조건' 칸을 클릭하세요."}
                                            </div>
                                        </div>
                                        <AdminButton variant="secondary" size="sm" className="border-purple-200 text-purple-700 hover:bg-purple-50" onClick={() => { setIsConditionMode(false); setConditionTargetId(null); }}>종료</AdminButton>
                                    </div>
                                )}
                            </div>

                            <div className="overflow-x-auto rounded-lg border border-gray-200">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">순서</th>
                                            <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">섹션</th>
                                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">내용</th>
                                            <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-36">표시 조건</th>
                                            <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">{isConditionMode ? '옵션 선택' : '유형 (옵션)'}</th>
                                            <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-36">관리</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {questions.map((q, idx) => {
                                            const isExpanded = expandedQId === q.id;
                                            const isTarget = isConditionMode && conditionTargetId === q.id;
                                            const targetQ = questions.find(t => t.id === conditionTargetId);
                                            const isCandidate = isConditionMode && conditionTargetId && q.sortOrder < (targetQ?.sortOrder || 0);
                                            const rowDraft = rowDrafts[q.id] || { section: q.section || '', title: q.title || '' };
                                            const rowDirty = isRowDirty(q);
                                            const rowSaving = savingRowId === q.id;
                                            const onRowKeyDown = (e) => {
                                                if (e.key === 'Enter' && !isConditionMode) {
                                                    e.preventDefault();
                                                    handleSaveRow(q);
                                                }
                                            };

                                            return (
                                                <React.Fragment key={q.id}>
                                                    <tr
                                                        draggable={isReordering}
                                                        onDragStart={(e) => handleDragStart(e, idx)}
                                                        onDragOver={(e) => handleDragOver(e, idx)}
                                                        onDrop={(e) => handleDrop(e, idx)}
                                                        className={`transition-colors ${
                                                            rowDirty ? 'bg-yellow-50' :
                                                            (isTarget ? 'bg-purple-50 ring-2 ring-purple-400 ring-inset' :
                                                            (isExpanded ? 'bg-blue-50' :
                                                            (dragItemIndex === idx ? 'bg-blue-100' :
                                                            (isReordering ? 'bg-white hover:bg-gray-50' : 'bg-white hover:bg-gray-50'))))
                                                        }`}
                                                        style={{
                                                            cursor: isReordering ? 'move' : 'default',
                                                            opacity: (isConditionMode && conditionTargetId && !isTarget && !isCandidate) ? 0.4 : 1
                                                        }}
                                                        onClick={() => {
                                                            if (isConditionMode && !isTarget) {
                                                                if (idx === 0) { alert('첫 번째 질문은 항상 표시되어야 합니다.'); return; }
                                                                setConditionTargetId(q.id);
                                                            }
                                                        }}
                                                    >
                                                        <td className="px-3 py-4 text-center text-sm font-medium text-gray-900 border-r border-gray-100">
                                                            {isReordering ? <div className="flex items-center justify-center gap-2"><span className="text-gray-400">☰</span>{q.sortOrder}</div> : q.sortOrder}
                                                        </td>

                                                        <td className="px-3 py-4 text-center">
                                                            {isConditionMode ? (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200 shadow-sm whitespace-nowrap">
                                                                    {q.section}
                                                                </span>
                                                            ) : (
                                                                <input
                                                                    type="text"
                                                                    className={`block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-xs p-1.5 border text-center ${rowDraft.section !== q.section ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                                                                    value={rowDraft.section}
                                                                    onChange={(e) => handleRowFieldChange(q.id, 'section', e.target.value)}
                                                                    onKeyDown={onRowKeyDown}
                                                                    list="all-sections-list"
                                                                    disabled={rowSaving}
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-900">
                                                            {isConditionMode ? (
                                                                <div className="flex items-start gap-2">
                                                                    {idx === 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-800 whitespace-nowrap mt-0.5">Start</span>}
                                                                    <span className="font-semibold text-gray-800 leading-snug">{q.title}</span>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-2">
                                                                    {idx === 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-800 whitespace-nowrap shrink-0">Start</span>}
                                                                    <input
                                                                        type="text"
                                                                        className={`block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border font-semibold text-gray-800 ${rowDraft.title !== q.title ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                                                                        value={rowDraft.title}
                                                                        onChange={(e) => handleRowFieldChange(q.id, 'title', e.target.value)}
                                                                        onKeyDown={onRowKeyDown}
                                                                        disabled={rowSaving}
                                                                    />
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-4" style={{ cursor: isConditionMode ? 'pointer' : 'default' }}>
                                                            {(() => {
                                                                if (isConditionMode && isTarget) {
                                                                    return (
                                                                        <div className="flex flex-col items-center justify-center gap-2">
                                                                            <div className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-1 rounded shadow-sm">🎯 대상 선택됨</div>
                                                                            {q.conditionJson?.dependsOnQuestionId ? (
                                                                                <button className="text-xs text-red-600 hover:text-red-800 underline font-medium" onClick={(e) => { e.stopPropagation(); handleClearCondition(q.id); }}>조건 초기화</button>
                                                                            ) : <span className="text-xs text-gray-500">(항상 표시)</span>}
                                                                        </div>
                                                                    );
                                                                }
                                                                if (!q.conditionJson?.dependsOnQuestionId) return <div className="text-center"><span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-teal-100 text-teal-800">Always</span></div>;
                                                                
                                                                const parent = questions.find(pq => pq.id === q.conditionJson.dependsOnQuestionId);
                                                                const opt = parent?.options?.find(o => o.id === q.conditionJson.requiredOptionId);
                                                                
                                                                if (!parent) return <span className="text-xs font-bold text-red-600">🚫 오류 (대상없음)</span>;
                                                                if (q.conditionJson.requiredOptionId && !opt) return <div className="text-xs font-bold text-red-600">🚫 오류 (옵션삭제)</div>;

                                                                const isOrderInvalid = parent.sortOrder >= q.sortOrder;
                                                                return (
                                                                    <div className="text-xs text-center border border-gray-200 rounded p-1 bg-gray-50">
                                                                        {isOrderInvalid && <div className="text-red-600 font-bold mb-1 border-b border-red-200 pb-1">⚠️ 순서 오류</div>}
                                                                        <div className="text-gray-700 line-clamp-2" title={parent.title}><span className={`font-bold mr-1 ${isOrderInvalid ? 'text-red-600' : 'text-blue-600'}`}>Q{parent.sortOrder}</span>{opt ? `[${opt.label}]` : `[전체]`}</div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td className="px-3 py-4 text-center"
                                                            onClick={(e) => {
                                                                if (!isConditionMode) {
                                                                    e.stopPropagation();
                                                                    handleExpandToggle(q.id);
                                                                }
                                                            }}
                                                            style={{ cursor: isConditionMode ? 'default' : 'pointer' }}
                                                        >
                                                            {isConditionMode ? (
                                                                <div className="flex flex-wrap gap-1.5 justify-center">
                                                                    {q.options?.map(opt => (
                                                                        <button
                                                                            key={opt.id}
                                                                            disabled={!isCandidate}
                                                                            onClick={(e) => { e.stopPropagation(); handleSetCondition(conditionTargetId, q.id, opt.id); }}
                                                                            className={`px-2 py-1 text-xs rounded-full border transition-all ${
                                                                                !isCandidate ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed hidden' : 
                                                                                (targetQ?.conditionJson?.requiredOptionId === opt.id) ? 'bg-purple-600 text-white border-purple-600 font-bold shadow-md scale-105' : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:text-purple-700'
                                                                            }`}
                                                                        >
                                                                            {opt.label}
                                                                        </button>
                                                                    ))}
                                                                    {!isCandidate && <span className="text-xs text-gray-400 italic">선택 불가</span>}
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center justify-center gap-1.5 cursor-pointer bg-gray-50 border border-gray-200 rounded-md py-1 px-2 hover:bg-blue-50 transition-colors">
                                                                    <span className="text-xs font-semibold text-gray-700">
                                                                        {q.type === 'RADIO' && '단일'}
                                                                        {q.type === 'CHECKBOX' && '다중'}
                                                                        {q.type === 'SELECT' && '목록'}
                                                                        {q.type === 'NUMBER' && '숫자'}
                                                                        {q.type === 'TEXT' && '문자'}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500 font-medium bg-white px-1.5 rounded-sm border border-gray-200 shadow-sm">{q.options?.length || 0}</span>
                                                                    <svg className={`w-4 h-4 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                                                </div>
                                                            )}
                                                        </td>
                                                        {!isConditionMode && (
                                                            <td className="px-3 py-4 text-center">
                                                                <div className="flex flex-col gap-1.5 items-stretch">
                                                                    {rowDirty && (
                                                                        <>
                                                                            <AdminButton
                                                                                variant="primary"
                                                                                size="sm"
                                                                                fullWidth
                                                                                disabled={rowSaving}
                                                                                onClick={(e) => { e.stopPropagation(); handleSaveRow(q); }}
                                                                                title="Enter 또는 클릭으로 저장"
                                                                            >
                                                                                {rowSaving ? '저장 중…' : '저장'}
                                                                            </AdminButton>
                                                                            <button
                                                                                className="inline-flex items-center justify-center px-2 py-1 border border-amber-200 text-xs font-medium rounded text-amber-700 bg-amber-50 hover:bg-amber-100"
                                                                                onClick={(e) => { e.stopPropagation(); handleDiscardRow(q); }}
                                                                                disabled={rowSaving}
                                                                            >
                                                                                되돌리기
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                    <div className="flex flex-wrap gap-1.5 justify-center">
                                                                        <button className="inline-flex items-center px-2 py-1 border border-indigo-200 text-xs font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50" disabled={rowSaving} onClick={(e) => { e.stopPropagation(); handleDuplicate(q.id); }}>복제</button>
                                                                        <button className="inline-flex items-center px-2 py-1 border border-red-200 text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50" disabled={rowSaving} onClick={(e) => { e.stopPropagation(); handleDeleteQuestion(q.id); }}>삭제</button>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr onClick={(e) => e.stopPropagation()}>
                                                            <td colSpan="6" className="p-0 border-b-2 border-blue-500 bg-blue-50/30">
                                                                <QuestionDetailPanel
                                                                    question={q}
                                                                    versionId={versionId}
                                                                    optionLabels={optionLabelDrafts[q.id]}
                                                                    onLabelChange={(optId, val) => handleOptionLabelChange(q.id, optId, val)}
                                                                    onSaveRow={() => handleSaveRow(q)}
                                                                    onOptionsMutated={() => loadData(true)}
                                                                />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                        {/* Render NEW Question Row if editingQId === 'NEW' */}
                                        {editingQId === 'NEW' && (
                                            <tr className="bg-yellow-50 border-2 border-dashed border-yellow-400">
                                                <td className="px-3 py-4 text-center text-sm font-medium text-gray-900">{questions.length + 1}</td>
                                                <td className="px-3 py-4 text-center">
                                                    <input
                                                        type="text"
                                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-xs p-1.5 border text-center"
                                                        value={editFormData.section || ''}
                                                        onChange={(e) => handleInlineChange('section', e.target.value)}
                                                        list="all-sections-list"
                                                        placeholder="섹션"
                                                    />
                                                </td>
                                                <td className="px-3 py-4">
                                                    <input
                                                        type="text"
                                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                                                        value={editFormData.title || ''}
                                                        onChange={(e) => handleInlineChange('title', e.target.value)}
                                                        placeholder="새 질문 제목"
                                                    />
                                                </td>
                                                <td className="px-3 py-4 text-center text-xs text-gray-500">(생성 후 설정)</td>
                                                <td className="px-3 py-4">
                                                    <select
                                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-xs p-1.5 border bg-white"
                                                        value={editFormData.type || 'RADIO'}
                                                        onChange={(e) => handleInlineChange('type', e.target.value)}
                                                    >
                                                        <option value="RADIO">단일 선택 (Radio)</option>
                                                        <option value="CHECKBOX">다중 선택 (Checkbox)</option>
                                                        <option value="SELECT">목록 선택 (Select)</option>
                                                        <option value="NUMBER">숫자 입력 (Number)</option>
                                                        <option value="TEXT">문자 입력 (Text)</option>
                                                    </select>
                                                </td>
                                                <td className="px-3 py-4 text-center">
                                                    <div className="flex flex-col gap-1.5 items-center">
                                                        <AdminButton variant="primary" size="sm" fullWidth onClick={() => handleInlineSave(editFormData)}>저장</AdminButton>
                                                        <AdminButton variant="secondary" size="sm" fullWidth onClick={handleInlineCancel}>취소</AdminButton>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {!isConditionMode && !isReordering && (
                                <div className="mt-6 mb-10 text-center">
                                    <button
                                        className="w-full flex items-center justify-center px-6 py-4 border-2 border-dashed border-blue-300 shadow-sm text-base font-bold rounded-xl text-blue-600 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                                        onClick={handleAddQuestion}
                                    >
                                        <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                                        새로운 질문 추가하기
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'components' && (
                        <div className="p-2 sm:p-0">
                            <div className="flex justify-end gap-2 mb-4">
                                <AdminButton variant="primary" onClick={() => setEditingComponent({})}>
                                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                                    요소 추가
                                </AdminButton>
                            </div>
                            <div className="overflow-x-auto rounded-lg border border-gray-200">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">코드</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">명칭</th>
                                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">기본 단가</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">관리자 메모</th>
                                            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">관리</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {components.map(c => (
                                            <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{c.code}</td>
                                                <td className="px-6 py-4 text-sm text-gray-700">{c.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-mono font-medium">{c.defaultPrice ? parseInt(c.defaultPrice).toLocaleString() : '0'}</td>
                                                <td className="px-6 py-4 text-sm text-gray-500 italic">{c.adminMemo}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                    <div className="flex justify-center gap-2">
                                                        <AdminButton variant="secondary" size="sm" onClick={() => setEditingComponent(c)}>수정</AdminButton>
                                                        <AdminButton variant="danger" size="sm" onClick={() => handleDeleteComponent(c.id)}>삭제</AdminButton>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {components.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="px-6 py-12 text-center text-sm text-gray-500">
                                                    등록된 가격 요소가 없습니다.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {editingComponent && (
                    <ComponentEditor
                        component={editingComponent}
                        onSave={handleSaveComponent}
                        onCancel={() => setEditingComponent(null)}
                    />
                )}

                {showHelp && <EstimateHelpModal onClose={() => setShowHelp(false)} />}


            </div>
        </AdminLayout>
    );
};

export default EstimateDetail;
